/**
 * LyricPolisherPanel — Main display for Parliament Polish results.
 * Shows progress during reviewing, polished result, and agent reviews.
 */

import React from 'react';
import { X, Sparkles } from 'lucide-react';
import { Button, StatusDot, LoadingSpinner, Badge } from '@/components/ui';
import { LYRIC_WRITER_AGENTS } from '@/data/lyricOrchestratorAgents';
import { PolishReviewCard } from './PolishReviewCard';
import { PolishSynthesisDisplay } from './PolishSynthesisDisplay';
import type {
  PolishResult,
  PolishStatus,
  PolishProgressEvent,
} from '@/types/lyricOrchestrator.types';

interface LyricPolisherPanelProps {
  status: PolishStatus;
  isPolishing: boolean;
  result: PolishResult | null;
  error: string | null;
  progress: PolishProgressEvent | null;
  completedAgents: string[];
  onCancel: () => void;
  onReset: () => void;
  onAccept: () => void;
}

function getProgressText(progress: PolishProgressEvent | null, status: PolishStatus): string {
  if (status === 'polishing') return 'The Parliament Polisher is merging the best improvements...';
  if (!progress) return 'Starting Parliament Polish...';

  switch (progress.type) {
    case 'reviewing-start':
      return '4 specialist editors are reviewing lyrics in parallel...';
    case 'reviewer-complete':
      return `Reviewer completed: ${LYRIC_WRITER_AGENTS.find(a => a.id === progress.agentId)?.name || progress.agentId}`;
    case 'reviewer-failed':
      return `Reviewer failed: ${progress.error}`;
    case 'reviewing-complete':
      return `All reviews complete (${progress.successCount} reviewers succeeded)`;
    case 'polish-synthesis-start':
      return 'Merging the best improvements...';
    case 'polish-synthesis-complete':
      return 'Polished lyrics ready!';
    case 'error':
      return progress.message;
    default:
      return 'Processing...';
  }
}

export const LyricPolisherPanel: React.FC<LyricPolisherPanelProps> = ({
  status,
  isPolishing,
  result,
  error,
  progress,
  completedAgents,
  onCancel,
  onReset,
  onAccept,
}) => {
  return (
    <div className="space-y-6 bg-slate-900/40 p-6 rounded-2xl border-l-4 border-l-accent shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-amber-400" />
          <h3 className="text-sm font-black text-slate-200 uppercase tracking-wider">
            Parliament Polish
          </h3>
          {result && <Badge variant="success" size="xs">Complete</Badge>}
          {status === 'failed' && <Badge variant="danger" size="xs">Failed</Badge>}
          {status === 'aborted' && <Badge variant="warning-muted" size="xs">Cancelled</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {isPolishing && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              leftIcon={<X size={14} />}
              className="text-red-400 border-red-500/30 hover:bg-red-900/20"
            >
              Cancel
            </Button>
          )}
          {(status === 'complete' || status === 'failed' || status === 'aborted') && (
            <Button variant="outline" size="sm" onClick={onReset} className="text-slate-400">
              Dismiss
            </Button>
          )}
        </div>
      </div>

      {/* Progress while polishing */}
      {isPolishing && (
        <div className="space-y-3 bg-slate-900/50 rounded-lg p-4 border border-slate-800">
          <div className="flex items-center gap-2">
            <LoadingSpinner size="sm" />
            <span className="text-sm text-slate-400">
              {getProgressText(progress, status)}
            </span>
          </div>

          {/* Agent status dots */}
          <div className="flex flex-wrap gap-3">
            {LYRIC_WRITER_AGENTS.map(agent => {
              const isComplete = completedAgents.includes(agent.id);
              return (
                <div key={agent.id} className="flex items-center gap-1.5">
                  <StatusDot
                    color={isComplete ? 'success' : 'warning'}
                    size="sm"
                    pulse={!isComplete}
                  />
                  <span className={`text-[11px] ${isComplete ? 'text-slate-400' : 'text-slate-500'}`}>
                    {agent.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Polished synthesis at top */}
          <PolishSynthesisDisplay
            synthesis={result.synthesis}
            originalLyrics={result.originalLyrics}
            onAccept={onAccept}
          />

          {/* Agent reviews below */}
          <div>
            <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">
              Reviewer Assessments
              <span className="text-slate-600 ml-2">
                ({result.reviews.length} reviewers, {(result.totalDurationMs / 1000).toFixed(1)}s)
              </span>
            </h4>
            <div className="space-y-2">
              {result.reviews.map(review => {
                const agent = LYRIC_WRITER_AGENTS.find(a => a.id === review.agentId);
                if (!agent) return null;
                return (
                  <PolishReviewCard key={review.agentId} review={review} agent={agent} />
                );
              })}
            </div>
          </div>

          {/* Metadata footer */}
          <div className="flex items-center gap-3 text-[10px] text-slate-600 border-t border-slate-800 pt-3">
            <span>{result.agentCount} reviewers</span>
            <span className="text-slate-800">&middot;</span>
            <span>{(result.totalDurationMs / 1000).toFixed(1)}s</span>
            {result.failedAgents.length > 0 && (
              <>
                <span className="text-slate-800">&middot;</span>
                <span className="text-red-500">{result.failedAgents.length} failed</span>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};
